/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
*  Copyright 2021 Adobe
*  All Rights Reserved.
*
* NOTICE:  All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

import React, { PureComponent } from "react";

import PropTypes from "prop-types";

import { logging } from "dc-core";

import ErrorBoundaryCss from "./ErrorBoundary.css";

class ErrorBoundary extends PureComponent {
    static propTypes = {
        children: PropTypes.node.isRequired,
        dropinId: PropTypes.string.isRequired,
        renderError: PropTypes.func,
    };

    static defaultProps = {
        renderError: undefined,
    };

    constructor(props) {
        super(props);
        this.logger = logging.getLogger({
            context: "ErrorBoundary",
            dropinId: props.dropinId,
            userAgent: navigator.userAgent,
        });
        this.state = {
            hasError: false,
        };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        this.logger.error(error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            if (this.props.renderError) {
                return this.props.renderError();
            }
            return (
                <div className={ ErrorBoundaryCss.boundary }>
                    <div className={ ErrorBoundaryCss.header }>
                        Encountered an Unexpected Error
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;

export const withErrorBoundary =
    (WrappedComponent, dropinId) => {
        const WithErrorBoundary = ({
            renderError,
            ...propx
        }) => (
            <ErrorBoundary dropinId={ dropinId } renderError={ renderError } >
                <WrappedComponent { ...propx } />
            </ErrorBoundary>
        );
        WithErrorBoundary.propTypes = {
            renderError: PropTypes.func,
        };

        WithErrorBoundary.defaultProps = {
            renderError: undefined,
        };
        return WithErrorBoundary;
    };

